import * as React from 'react';
import { ChargifyCredentials } from '../../admin/components/tools/PricingWizard/types';

// Declare Chargify global type
declare global {
	interface Window {
		Chargify: new () => ChargifyInstance;
	}
}

interface ChargifyInstance {
	load: (config: ChargifyConfig) => void;
	token: (
		form: HTMLElement | null,
		onSuccess: (token: string) => void,
		onError: (error: ChargifyError) => void
	) => void;
}

interface ChargifyConfig {
	publicKey: string;
	serverHost: string;
	type: string;
	fields: Record<string, ChargifyFieldConfig>;
	style?: Record<string, Record<string, string>>;
}

interface ChargifyFieldConfig {
	selector: string;
	label?: string;
	placeholder?: string;
	required?: boolean;
	value?: string;
	maxlength?: string;
}

interface ChargifyError {
	message?: string;
	errors?: string[];
}

export interface ChargifyFormConfig {
	credentials: ChargifyCredentials | undefined;
	enabled: boolean;
	firstName?: string;
	lastName?: string;
}

interface ChargifyFormState {
	isReady: boolean;
	isLoading: boolean;
	error: string | null;
}

const CHARGIFY_SCRIPT_ID = 'chargify-js-script';
const CHARGIFY_SCRIPT_URL = 'https://js.chargify.com/latest/chargify.js';

export function useChargifyForm({ credentials, enabled, firstName, lastName }: ChargifyFormConfig) {
	const [state, setState] = React.useState<ChargifyFormState>({
		isReady: false,
		isLoading: false,
		error: null,
	});
	const chargifyRef = React.useRef<ChargifyInstance | null>(null);
	const isInitializedRef = React.useRef(false);

	// Load Chargify.js script and initialize form
	React.useEffect(() => {
		if (!enabled || !credentials || isInitializedRef.current) {
			return;
		}

		setState({ isReady: false, isLoading: true, error: null });

		const loadScript = (): Promise<void> => {
			return new Promise((resolve, reject) => {
				// Check if script already exists
				const existingScript = document.getElementById(CHARGIFY_SCRIPT_ID);
				if (existingScript) {
					// Script already loaded
					if (window.Chargify) {
						resolve();
					} else {
						existingScript.addEventListener('load', () => resolve());
						existingScript.addEventListener('error', () => reject(new Error('Failed to load Chargify.js')));
					}
					return;
				}

				// Create and append script
				const script = document.createElement('script');
				script.id = CHARGIFY_SCRIPT_ID;
				script.src = CHARGIFY_SCRIPT_URL;
				script.async = true;
				script.onload = () => resolve();
				script.onerror = () => reject(new Error('Failed to load Chargify.js'));
				document.head.appendChild(script);
			});
		};

		const initializeChargify = async () => {
			try {
				await loadScript();

				// Small delay to ensure DOM elements are ready
				await new Promise(resolve => setTimeout(resolve, 100));

				if (!window.Chargify) {
					throw new Error('Chargify.js not available');
				}

				// Verify DOM elements exist
				const selectors = [
					'#chargify-first-name',
					'#chargify-last-name',
					'#chargify-city',
					'#chargify-state',
					'#chargify-zip',
					'#chargify-card-number',
					'#chargify-month',
					'#chargify-year',
					'#chargify-cvv',
				];
				for (const selector of selectors) {
					if (!document.querySelector(selector)) {
						throw new Error(`DOM element not found: ${selector}`);
					}
				}

				chargifyRef.current = new window.Chargify();

				// Remove trailing slash from serverHost if present
				const serverHost = credentials.accountUrl.replace(/\/$/, '');

				chargifyRef.current.load({
					publicKey: credentials.publicKey,
					serverHost,
					type: 'card',
					fields: {
						firstName: {
							selector: '#chargify-first-name',
							label: 'First Name on Card',
							placeholder: 'First name',
							required: true,
							value: firstName || '',
						},
						lastName: {
							selector: '#chargify-last-name',
							label: 'Last Name on Card',
							placeholder: 'Last name',
							required: true,
							value: lastName || '',
						},
						city: {
							selector: '#chargify-city',
							label: 'City',
							placeholder: 'City',
							required: true,
							maxlength: '30',
						},
						state: {
							selector: '#chargify-state',
							label: 'State',
							placeholder: 'ST',
							required: true,
							maxlength: '2',
						},
						zip: {
							selector: '#chargify-zip',
							label: 'ZIP Code',
							placeholder: 'ZIP',
							required: true,
							maxlength: '5',
						},
						number: {
							selector: '#chargify-card-number',
							label: 'Card Number',
							placeholder: '•••• •••• •••• ••••',
							required: true,
						},
						month: {
							selector: '#chargify-month',
							label: 'Month',
							placeholder: 'MM',
							required: true,
						},
						year: {
							selector: '#chargify-year',
							label: 'Year',
							placeholder: 'YYYY',
							required: true,
						},
						cvv: {
							selector: '#chargify-cvv',
							label: 'CVV',
							placeholder: '•••',
							required: true,
						},
					},
					style: {
						field: {
							backgroundColor: '#fff',
							borderRadius: '4px',
							border: '1px solid #D3DBDE',
							padding: '10px 12px',
							width: '100%',
							boxSizing: 'border-box',
						},
						input: {
							fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							fontSize: '14px',
							color: '#333',
							width: '100%',
							boxSizing: 'border-box',
							outline: 'none',
						},
						label: {
							fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							fontSize: '14px',
							fontWeight: '600',
							color: '#333',
							marginBottom: '6px',
							display: 'block',
						},
						message: {
							fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							fontSize: '12px',
							color: '#dc2626',
							marginTop: '4px',
						},
					},
				});

				isInitializedRef.current = true;

				// Give Chargify time to render iframes
				await new Promise(resolve => setTimeout(resolve, 500));

				setState({ isReady: true, isLoading: false, error: null });
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : 'Failed to initialize payment form';
				setState({ isReady: false, isLoading: false, error: errorMessage });
			}
		};

		initializeChargify();

		return () => {
			// Cleanup on unmount
			chargifyRef.current = null;
			isInitializedRef.current = false;
		};
	}, [enabled, credentials, firstName, lastName]);

	// Token generation function
	const getToken = React.useCallback(async (): Promise<string> => {
		if (!chargifyRef.current) {
			throw new Error('Payment form not initialized');
		}

		const form = document.querySelector('#chargify-form') as HTMLElement;
		if (!form) {
			throw new Error('Payment form element not found');
		}

		const tokenPromise = new Promise<string>((resolve, reject) => {
			chargifyRef.current!.token(
				form,
				(token: string) => resolve(token),
				(error: ChargifyError) => {
					const errorMessage = error.errors?.join(', ') || error.message || 'Failed to generate payment token';
					reject(new Error(errorMessage));
				}
			);
		});

		let timeoutId: ReturnType<typeof setTimeout>;
		const timeoutPromise = new Promise<never>((_, reject) => {
			timeoutId = setTimeout(() => reject(new Error('Payment processing timed out. Please try again.')), 60000);
		});

		return Promise.race([tokenPromise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
	}, []);

	return {
		...state,
		getToken,
	};
}
